import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaShieldAlt, FaUserSecret, FaDatabase, FaCookieBite, FaLock } from 'react-icons/fa';
import HeaderLandingPage from '../components/HeaderLandingPage';

const Privacy = () => {
  // Initialise dark mode from localStorage or default to false
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('cashsule-theme');
    return savedTheme === 'dark';
  });
  
  // Toggle dark mode and save to localStorage
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('cashsule-theme', newDarkMode ? 'dark' : 'light');
  };
  
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
    
    // Set up dark mode for this component only
    const pageContainer = document.getElementById('privacy-page-container');
    
    if (isDarkMode) {
      pageContainer.classList.add('dark');
    } else {
      pageContainer.classList.remove('dark');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark');
    };
  }, [isDarkMode]);

  return (
    <div id="privacy-page-container" className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <HeaderLandingPage isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />

      {/* Content starts after the header with padding */}
      <div className="pt-16"> {/* Add padding equal to header height */}
        {/* Breadcrumb Navigation */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <Link
              to="/"
              className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="font-medium text-gray-700 dark:text-gray-300">Privacy Policy</span>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
            <div className="px-6 py-8">
              <div className="flex items-center mb-6">
                <FaShieldAlt className="h-8 w-8 text-primary-600 mr-3" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Privacy Policy</h1>
              </div>
              
              <p className="text-gray-700 dark:text-gray-300 mb-8">
                Last Updated: 18 April 2025
              </p>

              <div className="space-y-10">
                {/* Introduction */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Introduction</h2>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    Welcome to Cashsule. We respect your privacy and are committed to protecting your personal data. 
                    This Privacy Policy explains how we collect, use, and safeguard your information when you use our service.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    Cashsule is designed to help you track expenses and manage your finances without requiring any sensitive banking information.
                  </p>
                </section>

                {/* Information We Collect */}
                <section>
                  <div className="flex items-center mb-3">
                    <FaUserSecret className="h-5 w-5 text-primary-600 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Information We Collect</h2>
                  </div>
                  <div className="pl-7">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Personal Information</h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                      To provide our services, we collect:
                    </p>
                    <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 mb-4 space-y-1">
                      <li>Email address (for account creation and communication)</li>
                      <li>Name (for personalisation)</li>
                      <li>Password (stored in encrypted form)</li>
                    </ul>
                    
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Financial Information</h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                      Cashsule does not request or store:
                    </p>
                    <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 mb-2 space-y-1">
                      <li>Bank account details</li>
                      <li>Credit/debit card information</li>
                      <li>Account numbers or routing information</li>
                      <li>Online banking credentials</li>
                    </ul>
                    <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 p-4 my-4">
                      <p className="text-yellow-700 dark:text-yellow-300 font-medium">
                        Important: Never enter sensitive banking information into Cashsule. The app is designed to work without direct access to your financial accounts.
                      </p>
                    </div>
                  </div>
                </section>

                {/* How We Use Your Information */}
                <section>
                  <div className="flex items-center mb-3">
                    <FaDatabase className="h-5 w-5 text-primary-600 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">How We Use Your Information</h2>
                  </div>
                  <div className="pl-7">
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      We use the information we collect to:
                    </p>
                    <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1">
                      <li>Create and manage your account</li>
                      <li>Provide and maintain our service</li>
                      <li>Notify you about changes to our service</li>
                      <li>Allow you to participate in interactive features</li>
                      <li>Provide customer support</li>
                      <li>Improve our service</li>
                    </ul>
                  </div>
                </section>

                {/* Data Security */}
                <section>
                  <div className="flex items-center mb-3">
                    <FaLock className="h-5 w-5 text-primary-600 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Data Security</h2>
                  </div>
                  <div className="pl-7">
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      The security of your data is important to us, but remember that no method of transmission over the Internet, 
                      or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, 
                      we cannot guarantee its absolute security.
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      All user data is stored in secure cloud databases with encryption at rest and in transit.
                    </p>
                  </div>
                </section>

                {/* Cookies */}
                <section>
                  <div className="flex items-center mb-3">
                    <FaCookieBite className="h-5 w-5 text-primary-600 mr-2" />
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Cookies</h2>
                  </div>
                  <div className="pl-7">
                    <p className="text-gray-700 dark:text-gray-300">
                      We use cookies to improve your experience while using our service. These cookies help us remember your preferences and 
                      provide you with a more personalised experience. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                    </p>
                  </div>
                </section>

                {/* Changes to This Privacy Policy */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Changes to This Privacy Policy</h2>
                  <p className="text-gray-700 dark:text-gray-300">
                    We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page 
                    and updating the "Last Updated" date at the top of this policy. You are advised to review this Privacy Policy periodically for any changes.
                  </p>
                </section>

                {/* Contact Us */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Contact Us</h2>
                  <p className="text-gray-700 dark:text-gray-300">
                    If you have any questions about this Privacy Policy, please contact us at{' '}
                    <a 
                      href="mailto:cashsule.app@gmail.com" 
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      cashsule.app@gmail.com
                    </a>.
                  </p>
                </section>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              <Link to="/terms" className="text-primary-600 dark:text-primary-400 hover:underline">Terms of Service</Link> | 
              <Link 
                to="/" 
                className="text-primary-600 dark:text-primary-400 hover:underline ml-2"
              >
                Back to Home
              </Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Privacy;
